import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ProjectStatusBadge, budgetColor } from "@/mockups/ui";
import { toast } from "sonner";
import {
  ChevronLeft, Globe, Mail, Phone, MapPin, Building2, Send,
  MessageSquare, StickyNote, KeyRound, Pencil,
} from "lucide-react";

const TIMELINE_ICON = { meeting: MessageSquare, call: Phone, email: Mail, note: StickyNote };
const initials = (n) => (n || "?").split(" ").map((x) => x[0]).slice(0, 2).join("");

export default function EmpresaDetalle() {
  const { id } = useParams();
  const { can } = useAuth();
  const { data: company, loading, refetch } = useFetch(`/companies/${id}`);
  const [note, setNote] = useState("");
  const [noteType, setNoteType] = useState("note");
  const [posting, setPosting] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  const openNotes = () => { setNotesDraft(company?.notes || ""); setNotesOpen(true); };
  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.patch(`/companies/${id}`, {
        name: company.name, type: company.type, sector: company.sector || "",
        vat: company.vat || "", email: company.email || "", phone: company.phone || "",
        website: company.website || "", address: company.address || "",
        status: company.status || "active", tags: company.tags || [],
        notes: notesDraft,
      });
      toast.success("Observaciones guardadas");
      setNotesOpen(false);
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al guardar"); }
    finally { setSavingNotes(false); }
  };

  const addInteraction = async () => {
    if (!note.trim()) return;
    setPosting(true);
    try {
      await api.post("/interactions", { company_id: id, type: noteType, content: note });
      setNote("");
      toast.success("Interacción añadida");
      refetch();
    } catch (e) { toast.error("Error al guardar"); } finally { setPosting(false); }
  };

  if (loading || !company) {
    return <div className="flex justify-center py-20"><div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in-up">
      <Link to="/crm/empresas" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors duration-200" data-testid="back-to-companies">
        <ChevronLeft className="h-4 w-4" /> Empresas
      </Link>

      <Card className="border-border shadow-none p-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="h-16 w-16 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-heading font-extrabold text-xl shrink-0">
            {company.name.slice(0, 2).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="font-heading font-bold text-2xl tracking-tight">{company.name}</h2>
              <Badge variant="outline" className={company.type === "client" ? "border-transparent bg-primary/12 text-primary font-medium" : "border-transparent bg-gold/15 text-[#a9741c] dark:text-gold font-medium"}>
                {company.type === "client" ? "Cliente" : "Prospecto"}
              </Badge>
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-muted-foreground mt-2">
              {company.sector && <span className="inline-flex items-center gap-1.5"><Building2 className="h-4 w-4" /> {company.sector}</span>}
              {company.city && <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {company.city}</span>}
              {company.website && <span className="inline-flex items-center gap-1.5"><Globe className="h-4 w-4" /> {company.website}</span>}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-6 pt-6 border-t border-border">
          <div><div className="font-heading font-bold text-xl tabular-nums">{company.contacts.length}</div><div className="text-xs text-muted-foreground mt-0.5">Contactos</div></div>
          <div><div className="font-heading font-bold text-xl tabular-nums">{company.projects.length}</div><div className="text-xs text-muted-foreground mt-0.5">Proyectos</div></div>
          <div><div className="font-heading font-bold text-xl tabular-nums">{company.interactions.length}</div><div className="text-xs text-muted-foreground mt-0.5">Interacciones</div></div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card className="border-border shadow-none">
            <Tabs defaultValue="timeline">
              <div className="px-5 pt-4 border-b border-border">
                <TabsList className="bg-transparent p-0 h-auto gap-4">
                  <TabsTrigger value="timeline" className="px-0 pb-3 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none" data-testid="tab-timeline">Actividad</TabsTrigger>
                  <TabsTrigger value="contacts" className="px-0 pb-3 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none" data-testid="tab-contacts">Contactos</TabsTrigger>
                  <TabsTrigger value="projects" className="px-0 pb-3 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none" data-testid="tab-projects">Proyectos</TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="timeline" className="p-5 mt-0">
                {can("crm", "edit") && (
                  <div className="flex flex-col sm:flex-row gap-2 mb-6">
                    <Select value={noteType} onValueChange={setNoteType}>
                      <SelectTrigger className="w-full sm:w-36" data-testid="interaction-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="note">Nota</SelectItem>
                        <SelectItem value="call">Llamada</SelectItem>
                        <SelectItem value="meeting">Reunión</SelectItem>
                        <SelectItem value="email">Email</SelectItem>
                      </SelectContent>
                    </Select>
                    <Textarea placeholder="Añade una nota, llamada o reunión…" value={note} onChange={(e) => setNote(e.target.value)} className="min-h-[44px] resize-none flex-1" data-testid="interaction-input" />
                    <Button size="icon" className="shrink-0 self-end h-11 w-11" onClick={addInteraction} disabled={posting} data-testid="interaction-submit"><Send className="h-4 w-4" /></Button>
                  </div>
                )}
                {company.interactions.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">Sin interacciones todavía.</p>
                ) : (
                  <div className="relative pl-6 space-y-6 before:absolute before:left-[11px] before:top-1 before:bottom-1 before:w-px before:bg-border">
                    {company.interactions.map((t) => {
                      const Icon = TIMELINE_ICON[t.type] || StickyNote;
                      return (
                        <div key={t.id} className="relative">
                          <div className="absolute -left-6 top-0 h-6 w-6 rounded-full bg-card border border-border flex items-center justify-center"><Icon className="h-3 w-3 text-muted-foreground" /></div>
                          <p className="text-sm leading-relaxed">{t.content}</p>
                          <p className="text-xs text-muted-foreground mt-1">{t.author_name} · {new Date(t.created_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="contacts" className="p-5 mt-0 space-y-2">
                {company.contacts.length === 0 ? <p className="text-sm text-muted-foreground text-center py-6">Sin contactos.</p> :
                  company.contacts.map((c) => (
                    <div key={c.id} className="flex items-center gap-3 p-3 rounded-md border border-border">
                      <Avatar className="h-9 w-9"><AvatarFallback className="text-xs bg-muted">{initials(c.name)}</AvatarFallback></Avatar>
                      <div className="flex-1 min-w-0"><div className="text-sm font-medium">{c.name}</div><div className="text-xs text-muted-foreground">{c.position}</div></div>
                      <div className="hidden sm:flex flex-col items-end text-xs text-muted-foreground"><span>{c.email}</span><span>{c.phone}</span></div>
                    </div>
                  ))}
              </TabsContent>

              <TabsContent value="projects" className="p-5 mt-0 space-y-2">
                {company.projects.length === 0 ? <p className="text-sm text-muted-foreground text-center py-6">Sin proyectos.</p> :
                  company.projects.map((p) => {
                    const pct = p.budget_hours > 0 ? Math.round((p.hours_spent / p.budget_hours) * 100) : 0;
                    return (
                      <Link key={p.id} to={`/proyecto/${p.id}`} className="flex items-center gap-4 p-3 rounded-md border border-border hover:bg-muted/40 transition-colors duration-200">
                        <div className="flex-1 min-w-0"><div className="text-sm font-medium">{p.name}</div></div>
                        <ProjectStatusBadge status={p.status} />
                        <div className="w-28 hidden sm:block"><div className="h-1.5 rounded-full bg-muted overflow-hidden"><div className={`h-full rounded-full ${budgetColor(pct)}`} style={{ width: `${Math.min(pct, 100)}%` }} /></div></div>
                      </Link>
                    );
                  })}
              </TabsContent>
            </Tabs>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="border-border shadow-none p-5" data-testid="company-notes-card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-bold text-base flex items-center gap-2"><KeyRound className="h-4 w-4 text-primary" /> Observaciones</h3>
              {can("crm", "edit") && (
                <Button size="sm" variant="outline" className="gap-1.5 h-7" onClick={openNotes} data-testid="company-notes-edit-btn"><Pencil className="h-3.5 w-3.5" /> Editar</Button>
              )}
            </div>
            {company.notes && company.notes.trim() ? (
              <p className="text-sm whitespace-pre-wrap break-words leading-relaxed text-foreground/90" data-testid="company-notes-value">{company.notes}</p>
            ) : (
              <p className="text-sm text-muted-foreground">Sin observaciones. Anota aquí contraseñas, accesos o notas internas de la empresa.</p>
            )}
          </Card>

          <Card className="border-border shadow-none p-5">
            <h3 className="font-heading font-bold text-base mb-4">Datos de contacto</h3>
            <dl className="space-y-3 text-sm">
              {[
                { icon: Mail, label: "Email", value: company.email },
                { icon: Phone, label: "Teléfono", value: company.phone },
                { icon: Globe, label: "Web", value: company.website },
                { icon: MapPin, label: "Dirección", value: company.address },
              ].filter((d) => d.value).map((d, i) => (
                <div key={i} className="flex items-start gap-3">
                  <d.icon className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div><dt className="text-xs text-muted-foreground">{d.label}</dt><dd className="font-medium break-words">{d.value}</dd></div>
                </div>
              ))}
              {!company.email && !company.phone && !company.website && !company.address && <p className="text-sm text-muted-foreground">Sin datos de contacto.</p>}
            </dl>
          </Card>
        </div>
      </div>

      <Dialog open={notesOpen} onOpenChange={setNotesOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Observaciones de {company.name}</DialogTitle>
            <DialogDescription>Notas internas, accesos y contraseñas de la empresa.</DialogDescription>
          </DialogHeader>
          <Textarea
            value={notesDraft}
            onChange={(e) => setNotesDraft(e.target.value)}
            placeholder="Escribe aquí las observaciones…"
            className="min-h-[220px] resize-y"
            data-testid="company-notes-textarea"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNotesOpen(false)}>Cancelar</Button>
            <Button onClick={saveNotes} disabled={savingNotes} data-testid="company-notes-save-btn">{savingNotes ? "Guardando…" : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
